import React, { useRef, useState } from 'react';
import { cx } from '../cx';
import { Button } from './Button';

// D-09 §15.17 — 파일 입력(버튼형). 숨김 <input type=file> + Button 트리거 (§2-4)
// 접근성: 실제 <button> 트리거라 키보드 포커스·엔터 동작이 네이티브로 보장된다.
export interface FileInputProps extends React.HTMLAttributes<HTMLDivElement> {
  accept?: string;
  multiple?: boolean;
  disabled?: boolean;
  maxSizeMB?: number;
  onFiles: (files: File[]) => void;
  children?: React.ReactNode; // 트리거 라벨
  variant?: 'primary' | 'secondary' | 'etc' | 'ghost';
  size?: 'sm' | 'md' | 'lg';
}

export function FileInput({
  accept,
  multiple = false,
  disabled,
  maxSizeMB,
  onFiles,
  children = '파일 선택',
  variant = 'secondary',
  size = 'md',
  className = '',
  id, // 숨김 <input>에 부여 (label htmlFor 대상) — 루트 div로 넘기지 않는다
  ...props
}: FileInputProps) {
  const inputRef = useRef<HTMLInputElement>(null);
  const [error, setError] = useState('');

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(e.target.files ?? []);
    if (maxSizeMB != null) {
      const over = files.find((f) => f.size > maxSizeMB * 1024 * 1024);
      if (over) {
        setError(`파일 크기는 ${maxSizeMB}MB 이하여야 합니다`);
        e.target.value = '';
        return;
      }
    }
    setError('');
    onFiles(files);
    e.target.value = ''; // 같은 파일 재선택 허용
  };

  return (
    <div className={cx('file-input', className)} {...props}>
      <input
        ref={inputRef}
        id={id}
        type="file"
        accept={accept}
        multiple={multiple}
        disabled={disabled}
        hidden
        onChange={handleChange}
      />
      <Button
        type="button"
        variant={variant}
        size={size}
        disabled={disabled}
        onClick={() => inputRef.current?.click()}
      >
        {children}
      </Button>
      {error && <span className="help">{error}</span>}
    </div>
  );
}
